import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVault,
  faCubes,
  faPaperPlane,
  faBarChart,
  faWeight,
  faEdit,
  faCheckCircle,
  faScaleBalanced,
  faTractor,
  faDollar,
  faAlignLeft,
  faTruckLoading,
  faTruckMoving,
  faUsers,
  faExclamationTriangle,
  faMap,
  faCalendarAlt,
  faXmark,
  faChevronDown,
  faGear,
  faWrench,
  faPhone,
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import { NavItem, NavLink, Nav, Collapse } from "reactstrap";
import classNames from "classnames";
import { Link, useLocation } from "react-router-dom";
import AuthService from "../Services/AuthService";
import VersionService from "../Services/VersionService";
import { setTranslations, setDefaultLanguage, withTranslation } from 'react-multi-lang';
import { APP_VERSION } from "../version";

const SideBar = ({ isOpen, toggle, systemChecker, t, user: userProp }) => {

    const authService = new AuthService();
    const [config, setConfig] = useState(authService.getConfig() || false);
    const [user, setUser] = useState(userProp || authService.getUser() || {});
    const [updateAvailable, setUpdateAvailable] = useState(VersionService.isUpdateAvailable());
    const [serverVersion, setServerVersion] = useState(VersionService.getServerVersion());
    const location = useLocation();

    useEffect(() => {
        return VersionService.onUpdateAvailable((info) => {
            setUpdateAvailable(true);
            setServerVersion(info.serverVersion);
        });
    }, []);

    const currentUser = userProp || user || authService.getUser() || {};
    const currentConfig = config || authService.getConfig() || {};

    const hasRole = (...roles) => {
        const r1 = (currentUser.role || '').toLowerCase();
        const r2 = (currentUser.role2 || '').toLowerCase();
        const username = (currentUser.username || '').toLowerCase();

        return roles.some(role => {
            const r = role.toLowerCase();
            if (r === 'admin') {
                return (
                    r1 === 'admin' ||
                    r2 === 'admin' ||
                    r1 === 'administrator' ||
                    r2 === 'administrator' ||
                    r1 === 'superadmin' ||
                    r2 === 'superadmin' ||
                    username === 'admin'
                );
            }
            return r === r1 || r === r2;
        });
    };

    const isPhoneCenterUser = hasRole('phoneCenter') && !hasRole('admin', 'quarryManager', 'manager');
    const isBlocksActive = ['/volumes', '/dionysos', '/allVolumes', '/reassessment', '/stock'].includes(location.pathname);
    const isEssActive = ['/ess-settings', '/ess-management', '/phone-center'].includes(location.pathname) || (isPhoneCenterUser && location.pathname === '/');
    const isMachineryActive = !isPhoneCenterUser && ['/schedules', '/', '/quarries', '/machine-map', '/machine-types', '/machines', '/live-machines', '/machine-map-split', '/live-faults', '/live-damages', '/check-ins', '/faults'].includes(location.pathname);
    const isOffersAndSalesActive = ['/offerPackages', '/offers', '/costs', '/termPrices', '/sales', '/invoices'].includes(location.pathname);

    const [openSubmenus, setOpenSubmenus] = useState({
        blocks: isBlocksActive,
        ess: isEssActive,
        machinery: isMachineryActive,
        offersAndSales: isOffersAndSalesActive
    });

    useEffect(() => {
        let conf = authService.getConfig();
        let u = userProp || authService.getUser();
        if(conf){
            setConfig(conf);
        }
        if(u){
            setUser(u);
        }
    }, [systemChecker, userProp]);

    const isActive = (path) => location.pathname === path;

    useEffect(() => {
        setOpenSubmenus({
            blocks: isBlocksActive,
            ess: isEssActive,
            machinery: isMachineryActive,
            offersAndSales: isOffersAndSalesActive
        });
    }, [location.pathname]);

    const toggleSubmenu = (menuKey) => {
        setOpenSubmenus(prev => ({
            ...prev,
            [menuKey]: !prev[menuKey]
        }));
    };

    // Permission flags for Blocks Management
    const canSeeAllBlocks = hasRole('manager', 'admin', 'quarryManager', 'accountant') && !['5', '2'].includes(currentConfig?.whCode);
    const canSeeReassessment = hasRole('manager', 'admin', 'assessor') && !['5', '2'].includes(currentConfig?.whCode);
    const canSeeStock = hasRole('manager', 'admin', 'salesman', 'quarryManager', 'salesmanager') && !['5', '2'].includes(currentConfig?.whCode);
    const hasBlocksSubmenu = Boolean(currentConfig);

    // Permission flags for ESS
    const canSeeEssSettings = hasRole('admin', 'hrManager', 'hrManagement');
    const canSeeEssManagement = hasRole('admin', 'quarryManager', 'manager', 'hrManager', 'hrManagement');
    const canSeePhoneCenter = hasRole('admin', 'phoneCenter', 'hrManager', 'hrManagement', 'manager', 'quarryManager');
    const hasEssSubmenu = canSeeEssSettings || canSeeEssManagement;

    // Permission flags for Machinery Management
    const canSeeSchedule = hasRole('admin', 'quarryManager');
    const canSeeQuarries = hasRole('admin');
    const canSeeMachineMap = hasRole('admin');
    const canSeeMachineTypes = hasRole('admin', 'quarryManager', 'manager');
    const canSeeMachines = hasRole('admin');
    const canSeeLiveMachines = hasRole('admin', 'quarryManager');
    const canSeeLiveDamages = hasRole('admin', 'quarryManager', 'manager');
    const canSeeCheckIns = hasRole('admin');
    const canSeeFaults = hasRole('admin');
    const hasMachinerySubmenu = canSeeSchedule || canSeeQuarries || canSeeMachineMap || canSeeMachineTypes || canSeeMachines || canSeeLiveMachines || canSeeLiveDamages || canSeeCheckIns || canSeeFaults;

    // Permission flags for Offers and Sales
    const canSeeOfferPackages = hasRole('manager', 'admin') && Boolean(currentConfig?.whCode);
    const canSeeOffers = hasRole('manager', 'admin', 'salesman', 'salesmanager') && !['5', '2'].includes(currentConfig?.whCode);
    const canSeeInvoices = hasRole('manager', 'admin', 'weigher') && !['5', '2'].includes(currentConfig?.whCode);
    const canSeeCosts = hasRole('manager', 'admin') && currentConfig?.whCode === '5';
    const canSeeTermPrices = hasRole('manager', 'admin') && currentConfig?.whCode === '4';
    const canSeeSales = hasRole('manager', 'admin', 'salesman', 'salesmanager', 'weigher') && currentConfig?.whCode === '5';
    const hasOffersAndSalesSubmenu = canSeeOfferPackages || canSeeOffers || canSeeInvoices || canSeeCosts || canSeeTermPrices || canSeeSales;

    return (
    <div className={classNames("sidebar", { "is-open": isOpen })}>
        <div className="sidebar-header">
            <div className="sidebar-logo-container">
                <img src="/logo512.png" alt="Nordia Logo" />
            </div>
            <button className="sidebar-close-btn" onClick={toggle} title="Close Sidebar">
                <FontAwesomeIcon icon={faXmark} />
            </button>
        </div>
        <div className="side-menu">
            <Nav vertical className="list-unstyled">

                {/* Submenu 1: Blocks Management */}
                { hasBlocksSubmenu && (
                    <li className={classNames("sidebar-submenu-group", { "is-open": openSubmenus.blocks, "has-active-child": isBlocksActive })}>
                        <button
                            type="button"
                            className={classNames("sidebar-submenu-toggle", { "is-open": openSubmenus.blocks, "is-child-active": isBlocksActive })}
                            onClick={() => toggleSubmenu('blocks')}
                        >
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faCubes} className="nav-icon" />
                                <span>{t('sideBar.blocksManagement') || 'Blocks Management'}</span>
                            </div>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={classNames("submenu-chevron", { "rotate": openSubmenus.blocks })}
                            />
                        </button>
                        <Collapse isOpen={openSubmenus.blocks}>
                            <ul className="sidebar-submenu-list list-unstyled">
                                { config.whCode !== '219' && (
                                    <NavItem className={isActive('/volumes') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/volumes"} className={isActive('/volumes') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faVault} className="nav-icon sub-icon" /> <span>{t('sideBar.volumeManagement')}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { config.whCode === '219' && (
                                    <NavItem className={isActive('/dionysos') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/dionysos"} className={isActive('/dionysos') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faVault} className="nav-icon sub-icon" /> <span>{t('sideBar.volumeManagement')}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeAllBlocks && (
                                    <NavItem className={isActive('/allVolumes') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/allVolumes"} className={isActive('/allVolumes') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faBarChart} className="nav-icon sub-icon" /> <span>{t('sideBar.allVolumes')}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeReassessment && (
                                    <NavItem className={isActive('/reassessment') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/reassessment"} className={isActive('/reassessment') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faCheckCircle} className="nav-icon sub-icon" /> <span>{t('sideBar.reassessment')}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeStock && (
                                    <NavItem className={isActive('/stock') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/stock"} className={isActive('/stock') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faScaleBalanced} className="nav-icon sub-icon" /> <span>{t('sideBar.stock')}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                            </ul>
                        </Collapse>
                    </li>
                )}

                {/* Submenu 2: ESS */}
                { hasEssSubmenu && (
                    <li className={classNames("sidebar-submenu-group", { "is-open": openSubmenus.ess, "has-active-child": isEssActive })}>
                        <button
                            type="button"
                            className={classNames("sidebar-submenu-toggle", { "is-open": openSubmenus.ess, "is-child-active": isEssActive })}
                            onClick={() => toggleSubmenu('ess')}
                        >
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faUsers} className="nav-icon" />
                                <span>{t('sideBar.ess') || 'ESS'}</span>
                            </div>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={classNames("submenu-chevron", { "rotate": openSubmenus.ess })}
                            />
                        </button>
                        <Collapse isOpen={openSubmenus.ess}>
                            <ul className="sidebar-submenu-list list-unstyled">
                                { canSeeEssSettings && (
                                    <NavItem className={isActive('/ess-settings') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/ess-settings"} className={isActive('/ess-settings') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faGear} className="nav-icon sub-icon" /> <span>{t('sideBar.essSettings') || 'ESS Settings'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeEssManagement && (
                                    <NavItem className={isActive('/ess-management') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/ess-management"} className={isActive('/ess-management') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faCalendarAlt} className="nav-icon sub-icon" /> <span>{t('sideBar.essManagement') || 'ESS Management'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeePhoneCenter && (
                                    <NavItem className={isActive('/phone-center') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/phone-center"} className={isActive('/phone-center') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faPhone} className="nav-icon sub-icon" /> <span>{t('sideBar.phoneCenter') || 'Phone Center'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                            </ul>
                        </Collapse>
                    </li>
                )}

                {/* Submenu 3: Machinery Management */}
                { hasMachinerySubmenu && (
                    <li className={classNames("sidebar-submenu-group", { "is-open": openSubmenus.machinery, "has-active-child": isMachineryActive })}>
                        <button
                            type="button"
                            className={classNames("sidebar-submenu-toggle", { "is-open": openSubmenus.machinery, "is-child-active": isMachineryActive })}
                            onClick={() => toggleSubmenu('machinery')}
                        >
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faTractor} className="nav-icon" />
                                <span>{t('sideBar.machineryManagement') || 'Machinery Management'}</span>
                            </div>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={classNames("submenu-chevron", { "rotate": openSubmenus.machinery })}
                            />
                        </button>
                        <Collapse isOpen={openSubmenus.machinery}>
                            <ul className="sidebar-submenu-list list-unstyled">
                                { canSeeSchedule && (
                                    <NavItem className={isActive('/schedules') || isActive('/') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/schedules"} className={isActive('/schedules') || isActive('/') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faCalendarAlt} className="nav-icon sub-icon" /> <span>{t('sideBar.schedules') || 'Schedule'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeQuarries && (
                                    <NavItem className={isActive('/quarries') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/quarries"} className={isActive('/quarries') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faMap} className="nav-icon sub-icon" /> <span>{t('sideBar.quarries') || 'Quarries'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeMachineMap && (
                                    <NavItem className={isActive('/machine-map') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/machine-map"} className={isActive('/machine-map') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faMap} className="nav-icon sub-icon" /> <span>{t('sideBar.machineMap') || 'Machines Map'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeMachineTypes && (
                                    <NavItem className={isActive('/machine-types') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/machine-types"} className={isActive('/machine-types') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faCubes} className="nav-icon sub-icon" /> <span>{t('sideBar.machineTypes') || 'Machine Types'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeMachines && (
                                    <NavItem className={isActive('/machines') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/machines"} className={isActive('/machines') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faTractor} className="nav-icon sub-icon" /> <span>{t('sideBar.machines') || 'Machines'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeLiveMachines && (
                                    <NavItem className={isActive('/live-machines') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/live-machines"} className={isActive('/live-machines') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faMap} className="nav-icon sub-icon" /> <span>{t('sideBar.liveMachines') || 'Map and List live'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeLiveDamages && (
                                    <NavItem className={isActive('/live-faults') || isActive('/live-damages') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/live-faults"} className={isActive('/live-faults') || isActive('/live-damages') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="nav-icon sub-icon" /> <span>{t('sideBar.damagesLive') || 'Damages Live'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeCheckIns && (
                                    <NavItem className={isActive('/check-ins') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/check-ins"} className={isActive('/check-ins') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faCheckCircle} className="nav-icon sub-icon" /> <span>{t('sideBar.checkIns') || 'Check Ins'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeFaults && (
                                    <NavItem className={isActive('/faults') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/faults"} className={isActive('/faults') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faWrench} className="nav-icon sub-icon" /> <span>{t('sideBar.faults') || 'Fault Management'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                            </ul>
                        </Collapse>
                    </li>
                )}

                {/* Submenu 4: Offer and Sales */}
                { hasOffersAndSalesSubmenu && (
                    <li className={classNames("sidebar-submenu-group", { "is-open": openSubmenus.offersAndSales, "has-active-child": isOffersAndSalesActive })}>
                        <button
                            type="button"
                            className={classNames("sidebar-submenu-toggle", { "is-open": openSubmenus.offersAndSales, "is-child-active": isOffersAndSalesActive })}
                            onClick={() => toggleSubmenu('offersAndSales')}
                        >
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faDollar} className="nav-icon" />
                                <span>{t('sideBar.offersAndSales') || 'Offer and Sales'}</span>
                            </div>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={classNames("submenu-chevron", { "rotate": openSubmenus.offersAndSales })}
                            />
                        </button>
                        <Collapse isOpen={openSubmenus.offersAndSales}>
                            <ul className="sidebar-submenu-list list-unstyled">
                                { canSeeOfferPackages && (
                                    <NavItem className={isActive('/offerPackages') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/offerPackages"} className={isActive('/offerPackages') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faPaperPlane} className="nav-icon sub-icon" /> <span>{t('sideBar.offerPackages') || 'Πακέτα Προσφορών'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeOffers && (
                                    <NavItem className={isActive('/offers') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/offers"} className={isActive('/offers') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faPaperPlane} className="nav-icon sub-icon" /> <span>{t('sideBar.offers')}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeInvoices && (
                                    <NavItem className={isActive('/invoices') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/invoices"} className={isActive('/invoices') ? 'active' : ''} disabled={!config}>
                                            <FontAwesomeIcon icon={faPaperPlane} className="nav-icon sub-icon" /> <span>{t('sideBar.vehicles')}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeCosts && (
                                    <NavItem className={isActive('/costs') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/costs"} className={isActive('/costs') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faDollar} className="nav-icon sub-icon" /> <span>{t('sideBar.costs') || 'Κόστος Μεταφορικών'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeTermPrices && (
                                    <NavItem className={isActive('/termPrices') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/termPrices"} className={isActive('/termPrices') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faDollar} className="nav-icon sub-icon" /> <span>{t('sideBar.termPrices') || 'Κόστος Όρων Παράδοσης'}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                                { canSeeSales && (
                                    <NavItem className={isActive('/sales') ? 'active' : ''}>
                                        <NavLink tag={Link} to={"/sales"} className={isActive('/sales') ? 'active' : ''}>
                                            <FontAwesomeIcon icon={faDollar} className="nav-icon sub-icon" /> <span>{t('sideBar.sales')}</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                            </ul>
                        </Collapse>
                    </li>
                )}

                {/* Standalone Navigation Items */}
                { currentConfig?.whCode === '219' && (
                    <NavItem className={isActive('/shelves') ? 'active' : ''}>
                        <NavLink tag={Link} to={"/shelves"} className={isActive('/shelves') ? 'active' : ''} disabled={!config}>
                            <FontAwesomeIcon icon={faAlignLeft} className="nav-icon" /> <span>Πατάρια</span>
                        </NavLink>
                    </NavItem>
                )}
                { hasRole('manager', 'admin', 'quarryManager', 'accountant') && !['5', '2'].includes(currentConfig?.whCode) && (
                    <NavItem className={isActive('/stats') ? 'active' : ''}>
                        <NavLink tag={Link} to={"/stats"} className={isActive('/stats') ? 'active' : ''} disabled={!config}>
                            <FontAwesomeIcon icon={faBarChart} className="nav-icon" /> <span>{t('sideBar.stats')}</span>
                        </NavLink>
                    </NavItem>
                )}
                { hasRole('manager', 'admin') && ['5', '2'].includes(currentConfig?.whCode) && (
                    <NavItem className={isActive('/vehicles') ? 'active' : ''}>
                        <NavLink tag={Link} to={"/vehicles"} className={isActive('/vehicles') ? 'active' : ''}>
                            <FontAwesomeIcon icon={faTractor} className="nav-icon" /> <span>{t('sideBar.vehiclesManagement')}</span>
                        </NavLink>
                    </NavItem>
                )}
                { hasRole('admin', 'weigher') && currentConfig?.whCode === '2' && (
                    <NavItem className={isActive('/incoming-deliveries') ? 'active' : ''}>
                        <NavLink tag={Link} to={"/incoming-deliveries"} className={isActive('/incoming-deliveries') ? 'active' : ''} disabled={!config}>
                            <FontAwesomeIcon icon={faTruckLoading} className="nav-icon" /> <span>{t('sideBar.incomingDeliveries')}</span>
                        </NavLink>
                    </NavItem>
                )}
                { currentConfig?.whCode === '219' && (
                    <NavItem className={isActive('/aggregate-shipments') ? 'active' : ''}>
                        <NavLink tag={Link} to={"/aggregate-shipments"} className={isActive('/aggregate-shipments') ? 'active' : ''} disabled={!config}>
                            <FontAwesomeIcon icon={faTruckMoving} className="nav-icon" /> <span>{t('aggregateShipments')}</span>
                        </NavLink>
                    </NavItem>
                )}
                { canSeePhoneCenter && !hasEssSubmenu && (
                    <NavItem className={isActive('/phone-center') || (isPhoneCenterUser && isActive('/')) ? 'active' : ''}>
                        <NavLink tag={Link} to={"/phone-center"} className={isActive('/phone-center') || (isPhoneCenterUser && isActive('/')) ? 'active' : ''}>
                            <FontAwesomeIcon icon={faPhone} className="nav-icon" /> <span>{t('sideBar.phoneCenter') || 'Phone Center'}</span>
                        </NavLink>
                    </NavItem>
                )}
                { hasRole('admin', 'hrManager', 'hrManagement', 'IT') && (
                    <NavItem className={isActive('/users') ? 'active' : ''}>
                        <NavLink tag={Link} to={"/users"} className={isActive('/users') ? 'active' : ''}>
                            <FontAwesomeIcon icon={faUsers} className="nav-icon" /> <span>{t('users.userManagement')}</span>
                        </NavLink>
                    </NavItem>
                )}
                <NavItem className="sidebar-version-item">
                    <div className="sidebar-version nav-link" style={{ cursor: 'default', color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.8125rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <FontAwesomeIcon icon={faInfoCircle} className="nav-icon" style={{ opacity: 0.6 }} /> <span>{t('sideBar.version') || t('version') || 'Έκδοση'}: {APP_VERSION}</span>
                        </div>
                        {updateAvailable && (
                            <button
                                type="button"
                                onClick={() => VersionService.forceReloadWithoutCache()}
                                style={{
                                    backgroundColor: '#f59e0b',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    marginTop: '4px',
                                    textAlign: 'center'
                                }}
                            >
                                Νέα έκδοση ({serverVersion}) - Ανανέωση
                            </button>
                        )}
                    </div>
                </NavItem>
            </Nav>
        </div>
        <div className="sidebar-footer">
            <span>Powered By</span>
            <a href="https://ekapps.gr" target="_blank" rel="noopener noreferrer">
                <img src="/ekapps-logo.png" alt="EKApps" style={{ width: '85px', height: 'auto', maxHeight: '48px', objectFit: 'contain' }} />
            </a>
        </div>
    </div>
    )
};

export default withTranslation(SideBar);
